/**
 * Grabadora de jugadas: los ultimos segundos de partido, siempre en memoria.
 *
 * Guarda POSICIONES, no acciones. Es lo contrario de lo que quiere el
 * entrenamiento por refuerzo, y es a proposito: un clip guardado como inputs
 * hay que volver a simularlo para verlo, asi que **se rompe en cuanto cambia
 * una constante del juego**. Y las constantes de Golazo se tocan cada semana.
 * Un clip de posiciones es literalmente lo que paso y sobrevive a cualquier
 * ajuste posterior.
 *
 * El coste es asumible: a 30 muestras/s, 8 segundos de 11v11 ocupan ~22 KB en
 * enteros de 16 bits, y las coordenadas del campo (0..1600, 0..1000) caben
 * exactas en ese rango.
 */
import type { Ball, Player, TeamSide } from "@golazo/shared";

/** Muestras por segundo. 30 basta para que el ojo lo lea como movimiento. */
export const CLIP_FPS = 30;
/** Segundos de jugada que se guardan hacia atras. */
export const CLIP_SECONDS = 8;
/** Sube si cambia el formato guardado; los clips viejos se descartan. */
export const CLIP_VERSION = 1;

export interface ClipPlayerInfo {
  id: string;
  side: TeamSide;
  shirtNumber: number;
  nickname: string | null;
}

export interface Clip {
  version: number;
  fps: number;
  /** Numero de muestras guardadas. */
  frames: number;
  players: ClipPlayerInfo[];
  /**
   * Posiciones aplanadas por muestra: [balonX, balonY, p0x, p0y, p1x, p1y, ...].
   * Enteros de 16 bits en coordenadas de campo.
   */
  data: Int16Array;
  /** Quien marco, si se sabe. Solo para el rotulo. */
  scorer: { nickname: string | null; shirtNumber: number } | null;
  assist: { nickname: string | null; shirtNumber: number } | null;
  side: TeamSide;
  atSecond: number;
}

export interface ClipRecorder {
  /** Avisa del paso del tiempo y toma muestra cuando toca. */
  sample(players: readonly Player[], ball: Ball, dt: number): void;
  /** Congela lo grabado hasta ahora. Devuelve null si no hay suficiente. */
  freeze(meta: Omit<Clip, "version" | "fps" | "frames" | "players" | "data">): Clip | null;
  /** Muestras guardadas ahora mismo. */
  readonly length: number;
  reset(): void;
}

export interface ClipRecorderOptions {
  fps?: number;
  seconds?: number;
}

const clampInt16 = (v: number): number => Math.max(-32768, Math.min(32767, Math.round(v)));

export function createClipRecorder(options: ClipRecorderOptions = {}): ClipRecorder {
  const fps = options.fps ?? CLIP_FPS;
  const seconds = options.seconds ?? CLIP_SECONDS;
  const capacity = Math.max(1, Math.round(fps * seconds));
  const interval = 1 / fps;

  /** Ancho de una muestra. Se fija con la primera, que ya conoce la plantilla. */
  let stride = 0;
  let ring: Int16Array | null = null;
  let players: ClipPlayerInfo[] = [];
  /** Indice de escritura dentro del anillo. */
  let head = 0;
  let filled = 0;
  let accumulator = 0;

  const capture = (list: readonly Player[], ball: Ball): void => {
    if (!ring) {
      players = list.map((p) => ({
        id: p.id,
        side: p.side,
        shirtNumber: p.shirtNumber,
        nickname: p.nickname,
      }));
      stride = 2 + list.length * 2;
      ring = new Int16Array(stride * capacity);
    }
    // La plantilla no cambia a mitad de partido; si cambiara, el clip perderia
    // el sentido, asi que se descarta lo grabado y se empieza de nuevo.
    if (list.length * 2 + 2 !== stride) {
      ring = null;
      head = 0;
      filled = 0;
      capture(list, ball);
      return;
    }

    let at = head * stride;
    ring[at++] = clampInt16(ball.pos.x);
    ring[at++] = clampInt16(ball.pos.y);
    for (const p of list) {
      ring[at++] = clampInt16(p.pos.x);
      ring[at++] = clampInt16(p.pos.y);
    }
    head = (head + 1) % capacity;
    filled = Math.min(filled + 1, capacity);
  };

  return {
    sample(list, ball, dt) {
      accumulator += dt;
      if (accumulator < interval) return;
      // Un solo fotograma por llamada aunque se acumule mas: tras un paron no
      // tiene sentido rellenar el anillo con copias de la misma posicion.
      accumulator = Math.min(accumulator - interval, interval);
      capture(list, ball);
    },

    get length() {
      return filled;
    },

    freeze(meta) {
      if (!ring || filled < fps) return null; // menos de un segundo no es jugada
      const out = new Int16Array(stride * filled);
      // El anillo se desenrolla al orden real: el mas viejo primero.
      const start = (head - filled + capacity) % capacity;
      for (let i = 0; i < filled; i++) {
        const from = ((start + i) % capacity) * stride;
        out.set(ring.subarray(from, from + stride), i * stride);
      }
      return {
        version: CLIP_VERSION,
        fps,
        frames: filled,
        players: players.map((p) => ({ ...p })),
        data: out,
        ...meta,
      };
    },

    reset() {
      head = 0;
      filled = 0;
      accumulator = 0;
    },
  };
}

/** Lee una muestra concreta: balon y jugadores en coordenadas de campo. */
export function clipFrameAt(
  clip: Clip,
  index: number,
): { ball: { x: number; y: number }; players: Array<{ x: number; y: number }> } {
  const stride = 2 + clip.players.length * 2;
  const at = Math.max(0, Math.min(clip.frames - 1, index)) * stride;
  const players: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < clip.players.length; i++) {
    players.push({ x: clip.data[at + 2 + i * 2]!, y: clip.data[at + 3 + i * 2]! });
  }
  return { ball: { x: clip.data[at]!, y: clip.data[at + 1]! }, players };
}

/** Duracion en segundos. */
export function clipDuration(clip: Clip): number {
  return clip.frames / clip.fps;
}
